/**
 * Typed access to the Google Cast sender SDK globals (present only in
 * Chrome once castable-video has loaded the SDK). Shared by the components
 * that need cast session state so each can self-serve it instead of
 * threading values between elements.
 */

import type { CastMediaSessionLike } from './cast-audio-tracks.ts';

type CastSenderGlobals = typeof globalThis & {
  cast?: {
    framework?: {
      CastContext?: {
        getInstance?: () => {
          getSessionState?: () => string;
          getCurrentSession?: () => {
            getSessionObj?: () => {
              media?: CastMediaSessionLike[] | null;
              sendMessage?: (
                namespace: string,
                message: Record<string, unknown>,
                onSuccess: () => void,
                onError: (error: unknown) => void,
              ) => void;
            };
            getCastDevice?: () => { friendlyName?: string | null };
          } | null;
        };
      };
      RemotePlayer?: new () => unknown;
      RemotePlayerController?: new (player: unknown) => {
        addEventListener(type: string, handler: () => void): void;
        removeEventListener(type: string, handler: () => void): void;
      };
      RemotePlayerEventType?: { MEDIA_INFO_CHANGED?: string };
      SessionState?: { SESSION_RESUMED?: string };
    };
  };
  chrome?: {
    cast?: {
      media?: {
        EditTracksInfoRequest?: new (
          activeTrackIds?: number[],
          textTrackStyle?: unknown,
        ) => unknown;
      };
    };
  };
};

function getCurrentSession() {
  return (globalThis as CastSenderGlobals)
    .cast?.framework?.CastContext?.getInstance?.()
    ?.getCurrentSession?.() ?? null;
}

/**
 * The chrome.cast.media.Media of the active cast session, if any.
 * A session accumulates one entry per LoadRequest, so after a replay the
 * FINISHED media session still sits at index 0 while the live one comes
 * later — return the newest non-IDLE entry, falling back to the newest
 * overall (which carries the IDLE/FINISHED state end-detection relies on).
 */
export function getCastMediaSession(): CastMediaSessionLike | null {
  try {
    const sessions = getCurrentSession()?.getSessionObj?.()?.media;
    if (!sessions?.length) return null;
    for (let i = sessions.length - 1; i >= 0; i--) {
      if (sessions[i] && sessions[i].playerState !== 'IDLE') {
        return sessions[i];
      }
    }
    return sessions[sessions.length - 1] ?? null;
  } catch (_error) {
    return null;
  }
}

/** Friendly name of the device the active cast session runs on, if any. */
export function getCastDeviceName(): string | null {
  try {
    return getCurrentSession()?.getCastDevice?.()?.friendlyName || null;
  } catch (_error) {
    return null;
  }
}

/** chrome.cast.media.EditTracksInfoRequest constructor, if available. */
export function getEditTracksInfoRequest() {
  return (globalThis as CastSenderGlobals)
    .chrome?.cast?.media?.EditTracksInfoRequest ?? null;
}

/**
 * Whether the current cast session was RESUMED (rejoined after a page
 * reload / origin-scoped join) rather than freshly started from this page's
 * cast prompt. The two flows need different local-playback handling.
 */
export function isCastSessionResumed(): boolean {
  try {
    const framework = (globalThis as CastSenderGlobals).cast?.framework;
    const state = framework?.CastContext?.getInstance?.()?.getSessionState?.();
    if (!state) return false;
    return state === (framework?.SessionState?.SESSION_RESUMED ??
      'SESSION_RESUMED');
  } catch (_error) {
    return false;
  }
}

const CAST_MEDIA_NAMESPACE = 'urn:x-cast:com.google.cast.media';

// Media-channel request ids only need to be unique within the session;
// start high to stay clear of the ids the SDK itself issues.
let castMediaRequestId = 10_000;

/**
 * Send the standard media-channel SET_PLAYBACK_RATE command. The web
 * sender SDK has no first-class API for playback rate, but this is the
 * exact message the Android/iOS sender SDKs emit from
 * RemoteMediaClient.setPlaybackRate(), and the CAF receiver handles it
 * natively — so every sender platform gets identical receiver behavior
 * with no receiver-side code. Returns false when there is no active media
 * session to talk to.
 */
export function setCastPlaybackRate(
  rate: number,
  onError: (error: unknown) => void = () => {},
): boolean {
  const session = getCurrentSession()?.getSessionObj?.();
  const mediaSessionId = getCastMediaSession()?.mediaSessionId;
  if (!session?.sendMessage || typeof mediaSessionId !== 'number') {
    return false;
  }
  try {
    session.sendMessage(
      CAST_MEDIA_NAMESPACE,
      {
        type: 'SET_PLAYBACK_RATE',
        requestId: castMediaRequestId++,
        mediaSessionId,
        playbackRate: rate,
      },
      () => {},
      onError,
    );
    return true;
  } catch (error) {
    onError(error);
    return false;
  }
}

/**
 * Subscribe to the sender framework's remote media-info updates (fires when
 * the receiver's MediaStatus changes, including the first one that carries
 * the track list after a load). Returns an unsubscribe function, or null
 * when the cast framework isn't available yet.
 */
export function onCastMediaInfoChanged(
  handler: () => void,
): (() => void) | null {
  const framework = (globalThis as CastSenderGlobals).cast?.framework;
  const RemotePlayer = framework?.RemotePlayer;
  const RemotePlayerController = framework?.RemotePlayerController;
  const eventType = framework?.RemotePlayerEventType?.MEDIA_INFO_CHANGED;
  if (!RemotePlayer || !RemotePlayerController || !eventType) return null;
  try {
    const controller = new RemotePlayerController(new RemotePlayer());
    controller.addEventListener(eventType, handler);
    return () => controller.removeEventListener(eventType, handler);
  } catch (_error) {
    return null;
  }
}
